import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  AbilityEffect,
  AbilityLimitation,
  AttachedTable,
  ConsumedResource,
  ExtraEffectsFeature, 
  SpellLimitation,
  UpgradableNumber,
  WeaponLimitation, 
} from "@dndModels";
import { 
  HAPPENS_WHEN,
  TIME_RESTRICTIONS 
} from "@dndModels/Constants";
import { HappensWhen, PotenceType, TimeRestriction } from "@dndModels/Types";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import WeaponLimitInput from "@dndComponents/limitations/WeaponLimitInput";
import AbilityLimitInput from "@dndComponents/limitations/AbilityLimitInput";
import SpellLimitInput from "@dndComponents/limitations/SpellLimitInput";
// import ToggleButtonBox from '../../../../components/input/buttons/ToggleButtonBox';
// import AbilityEffectInput from './AbilityEffectInput';
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
import StringBox from "@components/input/strings/StringBox";
// import SelectResourceBox from '../select/SelectResourceBox';
import EffectsInput from "@dndComponents/feature/EffectsInput";
import ConsumedResourceInput from "@dndComponents/feature/ConsumedResourceInput";
import { FeatureSource } from '@models';

interface Props { 
  obj: ExtraEffectsFeature;
  feature_source?: FeatureSource;
  onChange: (changed: ExtraEffectsFeature) => void;
  disabled: boolean;
  attached_table?: AttachedTable;
}

export default function ExtraEffectsFeatureInput({ 
  obj,
  feature_source,
  onChange,
  disabled,
  attached_table
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [ping, setPing] = useState(false);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  // console.log(obj);
  // console.log(obj);
  // const refresh_rule_helper = REFRESH_RULES.includes(obj.special_resource_refresh_rule) ? obj.special_resource_refresh_rule : "Special";
  return (
    <Grid container spacing={0} direction="row">
      <Grid size={3}>
        <SelectStringBox 
          name="Applies To"
          options={["Weapons","Abilities","Spells"]}
          values={obj.apply_to}
          multiple
          onChange={(changed: ("Weapons" | "Abilities" | "Spells")[]) => {
            const new_obj = obj.clone();
            new_obj.apply_to = changed;
            
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid size={3}>
        <SelectStringBox 
          name="How Often?"
          options={TIME_RESTRICTIONS}
          value={obj.time_restriction}
          onChange={(changed: TimeRestriction) => {
            const new_obj = obj.clone();
            new_obj.time_restriction = changed;
            
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid size={3}>
        <SelectStringBox 
          name="Happens When?"
          options={HAPPENS_WHEN}
          value={obj.happens_when}
          onChange={(changed: HappensWhen) => {
            const new_obj = obj.clone();
            new_obj.happens_when = changed;
            
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid size={3}>
        <StringBox 
          name="Extra Restriction"
          value={obj.extra_restriction}
          onChange={(changed: string) => {
            const new_obj = obj.clone();
            new_obj.extra_restriction = changed;
            
            onChange(new_obj);
          }}
        />
      </Grid>
      { obj.apply_to.includes("Weapons") && 
        <Grid size={12}>
          <WeaponLimitInput
            obj={obj.weapon_limit}
            disabled={disabled}
            onChange={(changed: WeaponLimitation) => {
              const new_obj = obj.clone();
              new_obj.weapon_limit = changed.clone();
              
              onChange(new_obj);
            }}
          />
        </Grid>
      }
      { obj.apply_to.includes("Abilities") && 
        <Grid size={12}>
          <AbilityLimitInput
            obj={obj.ability_limit}
            disabled={disabled}
            onChange={(changed: AbilityLimitation) => {
              const new_obj = obj.clone();
              new_obj.ability_limit = changed.clone();
              
              onChange(new_obj);
            }}
          />
        </Grid>
      }
      { obj.apply_to.includes("Spells") && 
        <Grid size={12}>
          <SpellLimitInput
            obj={obj.spell_limit}
            disabled={disabled}
            onChange={(changed: SpellLimitation) => {
              const new_obj = obj.clone();
              new_obj.spell_limit = changed.clone();
              
              onChange(new_obj);
            }}
          />
        </Grid>
      }
      <Grid
        size={{
          xs: obj.potence_type === "Class" ? 6 : 12
        }}>
        <SelectStringBox 
          name="Potence Type"
          options={["Slot","Character","Class","None"]}
          value={obj.potence_type} 
          disabled={disabled}
          onChange={(changed: PotenceType) => {
            const floating_feature = obj.clone();
            floating_feature.potence_type = changed;
            onChange(floating_feature);
          }}
        />
      </Grid>
      { obj.potence_type === "Class" && 
        <Grid
          size={{
            xs: 6
          }}>
          <SelectObjectBox
            name="Potence Class"
            data_type="game_class"
            value={obj.potence_class}
            disabled={disabled}
            onChange={(changed: string) => {
              const floating_feature = obj.clone();
              floating_feature.potence_class = changed;
              onChange(floating_feature);
            }}
          />
        </Grid>
      }
      <Grid
        size={{
          xs: 6
        }}>
        <UpgradableNumberBox 
          name="Attack Bonus"
          value={obj.attack_bonus} 
          disabled={disabled}
          attached_table={attached_table}
          onChange={(changed: UpgradableNumber) => {
            const floating_feature = obj.clone();
            floating_feature.attack_bonus = changed;
            onChange(floating_feature);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <UpgradableNumberBox 
          name="Save DC"
          value={obj.save_dc} 
          disabled={disabled}
          attached_table={attached_table}
          onChange={(changed: UpgradableNumber) => {
            const floating_feature = obj.clone();
            floating_feature.save_dc = changed;
            onChange(floating_feature);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <EffectsInput
          effects={obj.effects}
          disabled={disabled}
          for_obj={obj}
          feature_source={feature_source}
          onChange={(changed: AbilityEffect[]) => {
            const new_obj = obj;
            obj.effects = changed;
            onChange(new_obj);
            
            setPing(!ping);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <ConsumedResourceInput 
          obj={obj.consumed_resource} 
          disabled={false}
          onChange={(changed: ConsumedResource) => {
            const new_obj = obj;
            new_obj.consumed_resource = changed;
            onChange(new_obj);
            setReloading(true);
          }}
        />
      </Grid>
    </Grid>
  );
}
